import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api, errDetail } from "@agent/lib/api";
import { DraftRow } from "@agent/components/DraftRow";
import { VersionsModal } from "@agent/components/VersionsModal";

function confirmCascadeDelete(draft) {
  const published = !!(draft?.blog_url || draft?.linkedin_post_id);
  if (!published) return window.confirm("Delete this draft?") ? false : null;
  const parts = [];
  if (draft?.blog_url) parts.push("website blog post");
  if (draft?.linkedin_post_id) parts.push("LinkedIn post");
  return window.confirm(
    `This draft is published. Also delete the ${parts.join(" and ")}?\n\n` +
    `OK  → remove from ${parts.join(" and ")} AND delete the draft\n` +
    `Cancel → keep the published content, delete only the draft record`
  );
}

function deleteResultToast(data) {
  if (data?.blog?.ok || data?.linkedin?.ok) {
    const bits = [];
    if (data.blog?.ok) bits.push("blog");
    if (data.linkedin?.ok) bits.push("LinkedIn");
    toast.success(`Draft + ${bits.join(" + ")} deleted`);
  } else if (data?.blog?.ok === false || data?.linkedin?.ok === false) {
    const errs = [];
    if (data.blog?.ok === false) errs.push(`blog: ${data.blog.message}`);
    if (data.linkedin?.ok === false) errs.push(`linkedin: ${data.linkedin.message}`);
    toast.warning(`Draft deleted, but: ${errs.join("; ")}`);
  } else {
    toast.success("Draft deleted");
  }
}

export default function Library() {
  const [drafts, setDrafts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [versionsOf, setVersionsOf] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/drafts").then((r) => { setDrafts(r.data); setLoaded(true); }).catch((e) => toast.error(errDetail(e))), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    const draft = drafts.find((x) => x.id === id);
    const cascade = confirmCascadeDelete(draft);
    if (cascade === null) return; // user cancelled the plain "delete this draft?" prompt
    try {
      const { data } = await api.delete(`/drafts/${id}${cascade ? "?cascade=true" : ""}`);
      setDrafts(drafts.filter((x) => x.id !== id));
      deleteResultToast(data);
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  const showVersions = async (id) => {
    try {
      const { data } = await api.get(`/drafts/${id}`);
      setVersionsOf(data);
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-white text-[11px] tracking-[0.15em] uppercase text-slate-500 font-semibold mb-4 shadow-sm">Draft library</span>
      <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-[#0f172a] mb-8">
        All drafts. <span className="grad-text">Full history.</span>
      </h1>
      {loaded && drafts.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm">
          <p className="text-slate-500 text-sm">No drafts yet.</p>
          <button data-testid="library-new-draft-btn" onClick={() => navigate("/agent")}
            className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#0f172a] text-white text-sm font-medium hover:bg-[#1e293b] transition-colors duration-150">
            Create first draft <ArrowRight size={14} />
          </button>
        </div>
      )}
      <div className="space-y-3" data-testid="draft-list">
        {drafts.map((d) => (
          <DraftRow key={d.id} draft={d}
            onOpen={(id) => navigate(`/agent/composer/${id}`)}
            onShowVersions={showVersions}
            onDelete={remove} />
        ))}
      </div>

      <VersionsModal draft={versionsOf} onClose={() => setVersionsOf(null)} />
    </div>
  );
}
